import 'babel-polyfill'

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import App from './App'
import router from './router'
import store from './store'
import axios from 'axios'
import BootstrapVue from 'bootstrap-vue'
import LangFiles from './locales/en'
import moment from 'moment'
import Vuelidate from 'vuelidate'
import VueWait from 'vue-wait'
import VueI18n from 'vue-i18n'

import './assets/favicons/favicon.js'

import './assets/fa-solid.js'
import './assets/fontawesome.js'
import './assets/fa-svg-with-js.css'

// Import the styles directly. (Or you could add them via script tags.)'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap-vue/dist/bootstrap-vue.min.css'
import './assets/custom.css'

import Analytics from './utility/analyticsUtil'
import environment from './utility/environmentUtils'
import isEmpty from './utility/isEmpty'
import Logger from './utility/loggerUtil'
import Router from './utility/routerUtil'

import StartupService from './services/startupService.js'

import { AnalyticsConstants, SettingsConstants } from './constants.js'

Vue.use(BootstrapVue)
Vue.use(Vuelidate)
Vue.use(VueWait)
Vue.use(VueI18n)

Object.defineProperty(Vue.prototype, '$http', { value: axios }) // Makes the property readonly so it does not get accidentally overwritten

Vue.config.productionTip = environment.environment !== 'production' && environment.environment !== 'prod'
Vue.config.devtools = environment.environment !== 'production' && environment.environment !== 'prod'

export const i18n = new VueI18n({
  locale: SettingsConstants.defaultLanguage,
  fallbackLocale: SettingsConstants.defaultLanguage,
  silentFallbackWarn: true,
  LangFiles
})

const app = {
  initialize () {
    this.bindEvents()
    this.setupVue()
  },

  bindEvents () {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'wheel'] // Events to detect inactivity timeout on browser
    events.forEach(name => {
      document.addEventListener(name, this.onUserActivity)
    })

    window.addEventListener('focus', this.onWindowFocus)
    window.addEventListener('beforeinstallprompt', this.pwaBeforeChromeInstall) // This only fires on Chromium based browsers (not Safari)
    window.addEventListener('appinstalled', this.pwaAfterChromeInstall) // This fires on Chromium based browsers (not Safari)
  },

  /**
   * Captures the PWA install action and hides the banner.
   * @param {Object} e - The before install event.
   */
  pwaBeforeChromeInstall (e) {
    Logger.debug('PWA before Chromium install triggered')
    e.preventDefault()
    store.state.display.pwaDeferredPrompt = e // Storing the action to be used by our install button
  },

  /**
   * Records successful PWA installation.
   */
  async pwaAfterChromeInstall () {
    Logger.debug('PWA after Chromium installation')
    store.state.display.pwaDeferredPrompt = undefined // Clear the prompt so it can be garbage collected
  },

  onUserActivity () {
    const sessionTimeoutStart = store.state.session.sessionTimeoutStart
    if (isEmpty(sessionTimeoutStart) || moment().diff(sessionTimeoutStart, 'seconds') > 5) {
      app.receivedEvent('userActivity') // This is very annoying, watch out
      clearTimeout(app.inactivityTimer)
      store.commit('session/updateSessionTimeout', { timeoutStart: moment(), timeoutIsActive: true })
      app.inactivityTimer = setTimeout(app.handleSessionTimeout, (store.state.session.sessionTimeoutLimit * 60 * 1000) + 1000)
    }
  },

  onWindowFocus () {
    app.receivedEvent('onWindowFocus')
    if (app.isOverSessionTimeout()) {
      app.handleSessionTimeout()
    }
  },

  inactivityTimer: null,

  receivedEvent (id) {
    Logger.trace('Received Event: ' + id)
  },

  async handleSessionTimeout () {
    if (!store.state.session.sessionTimeoutIsActive) {
      return
    }

    const isApplicationFlow = Router.isRouteInFlow('Application')
    const isOverTimeout = app.isOverSessionTimeout()

    store.commit('session/updateSessionTimeout', { timeoutIsActive: false })

    if (isOverTimeout && !isApplicationFlow) {
      try {
        await store.dispatch('session/signout')
        Analytics.logEvent(AnalyticsConstants.eventName.logout, { method: AnalyticsConstants.method.logout.sessionTimeout }, { logMarketingEvent: true })
      } catch (e) {
        Logger.error('Error attempting to logout user.')
        Logger.debug(e)
      }

      router.push({ name: 'Login' })
      return
    }

    if (isApplicationFlow) {
      const scanIdMode = document.location.search.includes('scanIdDataUuid') || !isEmpty(router.currentRoute.query.scanIdDataUuid)

      // Don't do anything relating to session timeout if in specific pages
      if ((router.currentRoute.meta && router.currentRoute.meta.skipAuthTimeout === true) ||
          (router.currentRoute.name === 'PersonalData' && !store.state.session.authenticated) || scanIdMode) {
        Logger.debug('Skipping timeout for current page')
        store.commit('session/updateSessionTimeout', { timeoutStart: moment(), timeoutIsActive: true })
        return
      }

      if (isOverTimeout) {
        store.commit('session/setSessionTimeoutOver', { isOver: true })
        await store.dispatch('session/signout')
        Analytics.logEvent(AnalyticsConstants.eventName.logout, { method: AnalyticsConstants.method.logout.sessionTimeout }, { logMarketingEvent: true })
        router.push({ name: 'Login' })
      }

      sessionStorage.removeItem('customerData')
      sessionStorage.removeItem('vehicleData')
    }
  },

  isOverSessionTimeout () {
    const skipTimeout = store.state.session.sessionTimeoutSkip

    if (skipTimeout === true) {
      store.commit('session/updateSessionTimeout', { skipTimeout: false })
      return false
    }

    const minutesDifference = moment().diff(store.state.session.sessionTimeoutStart, 'minutes')

    Logger.debug(`Session timeout length (minutes): ${minutesDifference}`)

    return minutesDifference >= store.state.session.sessionTimeoutLimit
  },

  setupVue () {
    const vm = new Vue({ // eslint-disable-line no-unused-vars
      el: '#app',
      router,
      i18n,
      store,
      template: '<App/>',
      components: { App },
      wait: new VueWait({ useVuex: true }),

      async created () {
        await app.loadLangFiles()
        await StartupService.init()
        store.commit('session/setVueInstance', vm)
      }
    })
  },

  async loadLangFiles () {
    window.xlang = SettingsConstants.defaultLanguage

    return import('@/locales/en.json').then(
      messages => {
        i18n.setLocaleMessage(SettingsConstants.defaultLanguage, messages.default)
        i18n.locale = SettingsConstants.defaultLanguage
      }
    )
  }
}

app.initialize()
